/**
 * Date validation utilities for RewardAttributes component
 * Handles start date and end date validation with proper error messages
 */

import { getISOTimeString } from '@/utils/commons'

/**
 * Validates start date
 * @param {string} startDate - Start date in MM/DD/YYYY format
 * @param {string} endDate - End date in MM/DD/YYYY format (optional)
 * @returns {string|null} Error message or null if valid
 */
export const validateStartDate = (startDate, endDate = null) => {
  if (!startDate) {
    if (endDate) {
      return "Please select a start date before selecting an end date."
    }
    return null // Start date is not required unless end date is selected
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day
  
  const startDateObj = parseDateString(startDate)
  if (!startDateObj) {
    return "Invalid start date format."
  }

  // Check if start date is today or in the past
  if (startDateObj <= today) {
    return "Start date must be a future date."
  }

  return null
}

/**
 * Validates end date
 * @param {string} endDate - End date in MM/DD/YYYY format
 * @param {string} startDate - Start date in MM/DD/YYYY format
 * @returns {string|null} Error message or null if valid
 */
export const validateEndDate = (endDate, startDate = null) => {
  if (!endDate) {
    return "Please select an end date."
  }

  if (!startDate) {
    return "Please choose a start date first."
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day
  
  const endDateObj = parseDateString(endDate)
  const startDateObj = parseDateString(startDate)
  
  if (!endDateObj) {
    return "Invalid end date format."
  }

  if (!startDateObj) {
    return "Please choose a start date first."
  }

  // Check if end date is today or in the past
  if (endDateObj <= today) {
    return "End date must be a future date."
  }

  // Check if end date is before start date
  if (endDateObj < startDateObj) {
    return "End date cannot be earlier than the start date."
  }

  return null
}

/**
 * Parses date string in MM/DD/YYYY format to Date object
 * Uses the same logic as DatePickerInput component
 * @param {string} dateString - Date string in MM/DD/YYYY format
 * @returns {Date|null} Parsed date or null if invalid
 */
const parseDateString = (dateString) => {
  if (!dateString) return null
  
  const parts = dateString.split('/')
  if (parts.length !== 3) return null
  
  const month = parseInt(parts[0], 10)
  const day = parseInt(parts[1], 10)
  const year = parseInt(parts[2], 10)
  
  if (isNaN(month) || isNaN(day) || isNaN(year)) return null
  
  const date = new Date(year, month - 1, day)
  
  // Check if the date is valid
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  
  return date
}

/**
 * Converts date string to UTC format for API
 * Uses existing utility from commons.js
 * @param {string} dateString - Date string in MM/DD/YYYY format
 * @returns {string|null} UTC date string or null if invalid
 */
export const convertToUTC = (dateString) => {
  const date = parseDateString(dateString)
  if (!date) return null
  
  // Use existing utility for consistent UTC conversion
  return getISOTimeString(date, false, false)
}

/**
 * Validates both start and end dates together
 * @param {string} startDate - Start date in MM/DD/YYYY format
 * @param {string} endDate - End date in MM/DD/YYYY format
 * @returns {Object} Validation result with errors
 */
export const validateDates = (startDate, endDate) => {
  const startDateError = validateStartDate(startDate, endDate)
  const endDateError = validateEndDate(endDate, startDate)
  
  return {
    startDateError,
    endDateError,
    isValid: !startDateError && !endDateError
  }
}
